import { accentColorConfig } from '@hooks/useAccentColor'
import { Agent, Conversation, Message } from '@typings/dify'
import { AccentColor } from '@typings/index'
import { ChevronDown, FileTextIcon, MessageSquare, Paperclip, Send, Square } from 'lucide-react'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConversationSidebar from './ConversationSidebar'
import MessageBubble from './MessageBubble'
import TypingLoader from './TypingLoader'
import Loader from './Loader'
import { environment } from '@envs/environment'

interface ChatInterfaceProps {
  selectedAgent: Agent | null
  messages: Message[]
  onSendMessage: (content: string, conversationFileIds?: string[]) => void
  onLikeMessage: (messageId: string) => void
  selectedConversation: Conversation | null
  onSelectConversation: (conversation: Conversation) => void
  onDeleteConversation: (conversationId: string) => void
  onToggleFavorite: (conversationId: string) => void
  onRenameConversation: (conversationId: string, newTitle: string) => void
  onNewConversation: () => void
  onFileUpload: (files?: File[]) => void
  onStopGenerating?: () => void
  accentColor: AccentColor
  isAnswering: boolean
  onError: (error: string) => void
}

const ChatInterface = ({
  selectedAgent,
  messages,
  onSendMessage,
  onLikeMessage,
  selectedConversation,
  onSelectConversation,
  onDeleteConversation,
  onToggleFavorite,
  onRenameConversation,
  onNewConversation,
  onFileUpload,
  onStopGenerating,
  accentColor,
  isAnswering,
  onError,
}: ChatInterfaceProps) => {
  const [message, setMessage] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [showScrollButton, setShowScrollButton] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [alreadyUploaded, setAlreadyUploaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation()

  const allowedExtensions = [
    '.txt',
    '.md',
    '.markdown',
    '.pdf',
    '.html',
    '.xlsx',
    '.xls',
    '.docx',
    '.csv',
    '.eml',
    '.msg',
    '.pptx',
    '.ppt',
    '.xml',
    '.epub',
  ]

  //Vérifie si le type de fichier est autorisé
  const isFileAllowed = (file: File) => {
    const extension = file.name.includes('.') ? `.${file.name.split('.').pop()?.toLowerCase()}` : ''
    return allowedExtensions.includes(extension)
  }

  //Vérifie que le nombre de fichiers uploadés ne dépasse pas la variable environmentale MAX_FILE_UPLOAD 
  // et le nombre de fichiers déjà uploadés dans la conversation sélectionnée
  const isMaxFileUploadReached = (incomingFilesCount: number) => {
    const maxFileUpload = Number(environment.maxUploadFiles || 5)
    const conversationFiles = selectedConversation?.files?.length || 0
    return files.length + conversationFiles + incomingFilesCount > maxFileUpload
  }

  //Vérifie que la taille d'un fichier uploadés ne dépasse pas la variable environmentale MAX_TOTAL_FILE_SIZE_MB
  const isMaxFileSizeReached = (file: File) => {
    const maxFileSizeMB = Number(environment.maxFileSizeMB || 15)
    return file.size / (1024 * 1024) > maxFileSizeMB
  }

  const isSameFile = (a: File, b: File) => {
    return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified
  }
  
  //Traite les fichiers uploadés, vérifie qu'ils sont autorisés, que le nombre de fichiers et la taille des fichiers ne dépassent pas les variables environnementales
  const processIncomingFiles = (incomingFiles: File[]) => {
    if (!incomingFiles.length) return;

    const validFiles = incomingFiles.filter(isFileAllowed)
    
    if (validFiles.length !== incomingFiles.length) {
      onError(t('chat.invalid_file_type'))
      return;
    }
    if (isMaxFileUploadReached(validFiles.length)) {
      onError(t(`chat.max_file_upload_exceeded`, { maxFiles: environment.maxUploadFiles }))
      return;
    }
    if(isMaxFileSizeReached(validFiles[0])){
      onError(t(`chat.max_file_size_exceeded`, { maxSize: environment.maxFileSizeMB }))
      return;
    }
    //Filtre les fichiers déjà uploadés
    const uniqueFiles = validFiles.filter(
      (file) => !files.some((existing) => isSameFile(file, existing))
    )
    validFiles.length = 0
    validFiles.push(...uniqueFiles)

    //Si après les différents filtres il n'y a pas de fichiers valides, on ne fait rien
    if (!validFiles.length) return;

    //Met à jour le state local des fichiers et le state parent
    setFiles((prev) => {
      const next = [...prev, ...validFiles]
      onFileUpload?.(next)
      return next
    })
  }

  //Supprimer un fichier de la liste des fichiers uploadés, dans le state local, parent et dans l'input file
  const handleRemoveFile = (fileName: string, lastModified: number) => {
    //On met à jour le state local des fichiers
    setFiles((prev) => {
      const next = prev.filter(
        (file) => !(file.name === fileName && file.lastModified === lastModified)
      )
      //On met à jour l'input file
      if (fileInputRef.current) {
        const dataTransfer = new DataTransfer()
        next.forEach((file) => dataTransfer.items.add(file))
        fileInputRef.current.files = dataTransfer.files
        if (!next.length) {
          fileInputRef.current.value = ''
        }
      }
      //On met à jour le state des fichiers dans le parent
      onFileUpload?.(next)
      return next
    })
  }


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    setShowScrollButton(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    processIncomingFiles(Array.from(e.dataTransfer.files))
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    processIncomingFiles(Array.from(e.target.files ?? []))
    e.target.value = ''
  }

  const handleClickDragArea = () => {
    fileInputRef.current?.click() // Trigger the file input click
  }

  useEffect(() => {
    const hasFileUpload = messages.some((message) => message.files && message.files.length > 0)
    setAlreadyUploaded(hasFileUpload)
  }, [messages])

  useEffect(() => {
    const chatContainer = chatContainerRef.current
    if (!chatContainer) return

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = chatContainer
      const isAtBottom = scrollHeight - scrollTop <= clientHeight + 50

      setShowScrollButton(!isAtBottom)
    }

    chatContainer.addEventListener('scroll', handleScroll)
    return () => chatContainer.removeEventListener('scroll', handleScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatContainerRef.current])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  //Gérer l'affichage du loader sur l'interface du chat
  useEffect(() => {
    if (
      !selectedConversation ||
      messages.length ||
      selectedConversation &&
      Math.abs(new Date().getTime() - new Date(selectedConversation?.timestamp).getTime()) < 60000 //Si la conversation sélectionnée a été créer il y a moins d'une minute
    ) {
      setLoading(false)
    } else {
      setLoading(true)
    }
  }, [messages, selectedConversation])

  //Réinitialiser les fichiers uploadés lors du changement de conversation ou d'agents dans le state local, parent et dans l'input file
  useEffect(() => {
    if(files.length > 0){
      setFiles([])
      onFileUpload?.([])
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }     
    }
  }, [selectedConversation, selectedAgent])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (message.trim()) {
      onSendMessage(message)
      setMessage('')
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  const handleQuoteMessage = (content: string) => {
    setMessage(content)
    if (textareaRef.current) {
      textareaRef.current.focus()
    }
  }

  if (!selectedAgent) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400 mt-5">
        {t('chat.select_agent')}
      </div>
    )
  }

  return (
    <div className="flex-1 flex h-full">
      <ConversationSidebar
        agent={selectedAgent}
        selectedConversation={selectedConversation}
        onSelectConversation={onSelectConversation}
        onDeleteConversation={onDeleteConversation}
        onToggleFavorite={onToggleFavorite}
        onRenameConversation={onRenameConversation}
        onNewConversation={onNewConversation}
        accentColor={accentColor}
      />

      <div className="flex-1 flex flex-col relative">
        {selectedAgent.disclaimer && (
          <div className="p-4 bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400">{selectedAgent.disclaimer}</p>
          </div>
        )}
        {loading && (
          <Loader />
        )}
        <div ref={chatContainerRef} className="flex-1 min-h-0 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-800">
          {messages.map((msg, index) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              onQuoteMessage={() => {
                handleQuoteMessage(msg.content)
              }}
              onLike={msg.role === 'assistant' ? onLikeMessage : undefined}
              accentColor={accentColor}
              index={index}
            />
          ))}

          {messages[messages.length - 1] && messages[messages.length - 1].role === 'user' && (
            <TypingLoader accentColor={accentColor} />
          )}
          <div ref={messagesEndRef} />
          {showScrollButton && (
            <div
              className={`flex items-center justify-center absolute ${selectedAgent.supportsFileUpload && !files ? 'bottom-[180px]' : 'bottom-[120px]'} left-0 right-0 z-50 w-full`}
            >
              <button
                onClick={scrollToBottom}
                className={`p-3 rounded-full shadow-lg transition-transform ${
                  accentColorConfig[accentColor].primary
                } ${accentColorConfig[accentColor].hover} ${
                  accentColorConfig[accentColor].primary.includes('text-black') ? '' : 'text-white'
                }`}
                title={t('chat.scroll_to_latest')}
              >
                <ChevronDown size={20} />
              </button>
            </div>
          )}
        </div>

        {selectedConversation ? (
          <form onSubmit={handleSubmit} className="p-4 flex justify-center dark:bg-gray-800">
            <div className="max-w-3xl w-full">
              {files.length > 0 && (
                <div className="bg-slate-400 dark:bg-slate-600 rounded-t-sm mt-1 text-sm text-gray-100 dark:text-gray-200 ">
                  {files.map((file) => (
                    <div
                      key={`${file.name}-${file.lastModified}`}
                      className="group flex items-center gap-2 px-3 py-2 overflow-hidden cursor-pointer hover:bg-slate-500 dark:hover:bg-slate-700 transition-colors"
                      onClick={() => handleRemoveFile(file.name, file.lastModified)}
                    >
                      <FileTextIcon size={15} className="group-hover:opacity-50 transition-opacity" />
                      <span className="truncate group-hover:opacity-50 transition-opacity">{file.name}</span>
                      <span className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-xs">
                        {t('chat.delete_file')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex items-center p-2 bg-white dark:bg-gray-900 rounded-b-lg space-x-2">
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('chat.type_message')}
                  onKeyDown={(e) => {
                    if (isAnswering) return

                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSubmit(e)
                    }
                  }}
                  disabled={isAnswering}
                  className="flex-1 p-3 focus:outline-none focus:ring-0 bg-transparent dark:text-white placeholder:text-gray-500 resize-none"
                />
                {selectedAgent.supportsFileUpload &&  (
                  <div
                    className={`p-2 border-2 cursor-pointer border-dashed rounded-lg dark:text-white ${
                      isDragging ? 'border-gray-300 bg-gray-100 dark:bg-gray-900' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleClickDragArea}
                    >
                    <input
                      type="file"
                      multiple
                      ref={fileInputRef}
                      className="hidden"
                      accept={allowedExtensions.join(',')}
                      onChange={handleFileInputChange}
                    />
                    <Paperclip size={20}/>
                </div>
              )}
                  {isAnswering ? (
                    <button
                      type="button"
                      onClick={onStopGenerating}
                      className={`p-2 rounded-full ${accentColorConfig[accentColor].primary} text-white`}
                      title={t('chat.stop_generating')}
                    >
                      <Square size={20} />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={isAnswering}
                      className={`p-2 rounded-full ${accentColorConfig[accentColor].primary} ${accentColorConfig[accentColor].primary.includes('text-black') ? '' : 'text-white'}`}
                    >
                      <Send size={20} />
                    </button>
                  )}
                </div>
              {selectedAgent.supportsFileUpload && (
                <div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('chat.supported_file_types')}: {allowedExtensions.join(', ')} | {`${environment.maxFileSizeMB}`} {t('chat.file_info')}
                  </p>
                </div>
              )}
            </div>
          </form>
        ) : (
          <div className="p-8 flex items-center justify-center dark:bg-gray-800">
            <button
              onClick={onNewConversation}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg shadow-md transition-all ${accentColorConfig[accentColor].primary} ${accentColorConfig[accentColor].hover} ${accentColorConfig[accentColor].primary.includes('text-black') ? '' : 'text-white'}`}
            >
              <MessageSquare size={20} />
              <span className="font-semibold">{t('chat.start_new_conversation')}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
export default ChatInterface